import moment from 'moment-timezone';
import { DateTime } from 'luxon';
import { ResultOf } from '@graphql-typed-document-node/core';
import { JobDetailsFragment } from '../jobDetailsComponents/graphql';

const CLOCK_TIME_EDIT_THRESHOLD_HOURS = 2;

export function getDateBoundary(value?: string, timezone?: string) {
  if (!value) {
    return undefined;
  }

  return DateTime.fromISO(value, timezone ? { zone: timezone } : undefined);
}

export function getClockDateBoundaries(job: ResultOf<typeof JobDetailsFragment> | undefined) {
  if (!job) {
    return {
      clockInMinDate: undefined,
      clockInMaxDate: undefined,
      clockOutMinDate: undefined,
      clockOutMaxDate: undefined,
    };
  }

  const timezone = job.location?.timezone_lookup || moment.tz.guess();
  const startDate = getDateBoundary(job.starts_date, timezone);
  const endDate = getDateBoundary(job.ends_date, timezone);
  const endTime = getDateBoundary(job.ends_time, timezone);

  if (!startDate || !endDate) {
    return {
      clockInMinDate: undefined,
      clockInMaxDate: undefined,
      clockOutMinDate: undefined,
      clockOutMaxDate: undefined,
    };
  }

  const scheduledEnd = endTime
    ? endDate.set({
        hour: endTime.hour,
        minute: endTime.minute,
        second: endTime.second,
        millisecond: 0,
      })
    : endDate.endOf('day');
  const maxEditableDate = scheduledEnd
    .plus({ hours: CLOCK_TIME_EDIT_THRESHOLD_HOURS })
    .startOf('day');

  return {
    clockInMinDate: startDate.startOf('day'),
    clockInMaxDate: maxEditableDate,
    clockOutMinDate: startDate.startOf('day'),
    clockOutMaxDate: maxEditableDate,
  };
}

export const parseTimePickerValue = (value: unknown, timezone: string) => {
  if (!value) {
    return value;
  }

  // Anchor the picker to a fixed local date so 24h mode can freely change hours on mobile.
  const parsed = moment(value).tz(timezone);
  return moment
    .tz(
      {
        year: 2000,
        month: 0,
        date: 1,
        hour: parsed.hour(),
        minute: parsed.minute(),
        second: 0,
        millisecond: 0,
      },
      timezone,
    )
    .toISOString();
};

export const formatTimePickerValue = (value: unknown, fieldValue: unknown, timezone: string) => {
  if (!value) {
    return value;
  }

  // Keep the original local date, replace only hour/minute from picker selection, then store as UTC.
  const selectedTime = moment(value).tz(timezone);
  const base = fieldValue ? moment(fieldValue).tz(timezone) : moment.tz(timezone);

  return base
    .hour(selectedTime.hour())
    .minute(selectedTime.minute())
    .second(0)
    .millisecond(0)
    .utc()
    .toISOString();
};
